import React from "react";
import classNames from "classnames";
import { ItinerarySection } from "..";
import { Icomoon } from "../..";
import { LinkButton } from "../../misc";
import { AbtaLogo, AstaLogo, AtolLogo, Icon } from "../../brand";
import { PriceBreakdown } from "./PriceBreakdown";
import * as WebRoutes from "@travellocal/utils/webRoutes";
import "./ItineraryPricing.scss";

export interface ItineraryPricingProps {

  enquiryId: number;
  lto: Pick<ILtoSummary, "name" | "id" | "slug">;
  isBookableItinerary: boolean;
  totalPrice: IPriceInCurrency;

  className?: string;
  lineItems?: IEnquiryItineraryLineItem[];

  totalPaid?: IPriceInCurrency;
  depositPrice?: IPriceInCurrency;
  prepayment?: IEnquiryItineraryPrepayment;
  balanceDueDate?: IDate;
}

enum PricingStatus {
  NoPayment = 1,
  PartialPayment = 2,
  FullyPaid = 3,
  NoPrice = 4,
  ReissueRequired = 5,
}

export const ItineraryPricing: React.FC<ItineraryPricingProps> = ({
  className,
  enquiryId,
  lto,
  lineItems,
  totalPrice,
  totalPaid,
  prepayment,
  balanceDueDate,
  isBookableItinerary,
}) => {

  let status: PricingStatus;
  if (!isBookableItinerary) {
    status = PricingStatus.ReissueRequired;
  }
  else if (totalPrice == null || (totalPrice.price ?? 0) === 0 || lineItems == null || lineItems.length === 0) {
    status = PricingStatus.NoPrice;
  }
  else if (totalPaid != null && totalPaid.price > 0) {
    status = totalPaid.price < totalPrice.price ? PricingStatus.PartialPayment : PricingStatus.FullyPaid;
  }
  else {
    status = PricingStatus.NoPayment;
  }

  return (
    <ItinerarySection
      className={classNames("itinerary-pricing", className)}
      icomoon={Icomoon.cash2}
      title="Pricing details"
    >
      <PriceBreakdown
        lineItems={lineItems ?? []}
        totalPrice={totalPrice}
        totalPaid={totalPaid ?? { price: 0, currency: totalPrice.currency }}
        balanceDueDate={balanceDueDate}
        prepayment={prepayment}
      />

      {status === PricingStatus.NoPayment && (
        <div className="columns is-marginless itinerary-pricing__book">
          <div className="column">
            <div className="itinerary-pricing__trust has-text-right-mobile">
              <p className="text--label">Book with confidence...</p>

              <AtolLogo type="dark" height={40} />
              <AbtaLogo type="dark" height={40} />
              <AstaLogo type="dark" height={40} />
            </div>
          </div>
          <div className="column is-narrow has-text-right-mobile">
            <div className="itinerary-pricing__cta-wrap">
              <LinkButton
                buttonClass="primary-button"
                className="itinerary-pricing__cta is-large"
                to={WebRoutes.customerEnquiryMakePayment(enquiryId)}
                icomoon={Icomoon.power2}
                iconAlign="right"
              >
                Book now
              </LinkButton>

              <div className="itinerary-pricing__card-logos">
                <img src="/images/credit-cards/visa.svg" height="12" width="40" />
                <img src="/images/credit-cards/mastercard.svg" height="30" width="36" />
                <img src="/images/credit-cards/maestro.svg" height="30" width="36" />
              </div>
            </div>
          </div>
        </div>
      )}

      {(status === PricingStatus.PartialPayment || status === PricingStatus.FullyPaid) && (
        <div className="columns is-marginless">
          <div className="column">
            {status === PricingStatus.FullyPaid && (
              <span className="itinerary-pricing__paid">
                <Icon icomoon={Icomoon.checkmark3} /> You&apos;re all paid up!
              </span>
            )}
          </div>
          <div className="column has-text-right">
            <LinkButton
              buttonClass="secondary-button"
              to={WebRoutes.customerEnquiryPayment(enquiryId)}
            >
              Payment history
            </LinkButton>
          </div>
        </div>
      )}

      {status === PricingStatus.PartialPayment && (
        <div className="columns is-marginless itinerary-pricing__book">
          <div className="column">
          </div>
          <div className="column is-narrow has-text-right-mobile">
            <div className="itinerary-pricing__cta-wrap">
              <LinkButton
                buttonClass="primary-button"
                className="itinerary-pricing__cta is-large"
                to={WebRoutes.customerEnquiryMakePayment(enquiryId)}
              >
                Make another payment
              </LinkButton>
              <div className="itinerary-pricing__card-logos">
                <img src="/images/credit-cards/visa.svg" height="12" width="40" />
                <img src="/images/credit-cards/mastercard.svg" height="30" width="36" />
                <img src="/images/credit-cards/maestro.svg" height="30" width="36" />
              </div>
            </div>
          </div>
        </div>
      )}

      {status === PricingStatus.ReissueRequired && (
        <div className="columns itinerary-pricing__reissue">
          <div className="column">
            <h3>This is an old itinerary</h3>
            <p className="is-marginless">
              If you&apos;d like to book, please ask your expert to reissue <strong>Itinerary #2</strong>
            </p>
          </div>
          <div className="column is-narrow itinerary-pricing__reissue-cta">
            <LinkButton
              buttonClass="primary-button"
              className="itinerary-pricing__cta is-large"
              to={WebRoutes.customerEnquiryMessaging(enquiryId)}
            >
              Request reissue
            </LinkButton>
            <LinkButton
              buttonClass="primary-button"
              className="itinerary-pricing__cta is-large inverted"
              to={WebRoutes.customerEnquiryItinerary(enquiryId)}
            >
              View current itinerary
            </LinkButton>
          </div>
        </div>
      )}

      <ul className="itinerary-pricing__links">
        <li>
          <LinkButton buttonClass="secondary-button" to={WebRoutes.customerEnquiryBookingTerms(enquiryId)}>
            {lto.name} Terms and Conditions
          </LinkButton>
        </li>
        <li>
          <LinkButton buttonClass="secondary-button" to={WebRoutes.termsOfUse()}>
            TravelLocal Terms of Use
          </LinkButton>
        </li>
        <li>
          <LinkButton buttonClass="secondary-button" to={WebRoutes.termsAndConditions()}>
            TravelLocal Terms and Conditions
          </LinkButton>
        </li>
        <li>
          <LinkButton buttonClass="secondary-button" to={WebRoutes.ltoPtr(lto.slug)}>
            Package Travel and Linked Travel Arrangements Regulations
          </LinkButton>
        </li>
      </ul>
    </ItinerarySection>
  );
}
